import { useCallback, useEffect, useMemo, useState } from "react";

import { getIdentifier } from "@medplum/core";
import { Organization, ValueSetExpansionContains } from "@medplum/fhirtypes";
import { HEALTH_GORILLA_SYSTEM, TestCoding } from "@medplum/health-gorilla-core";
import { TestSearch } from "@medplum/health-gorilla-react";
import { useMedplum } from "@medplum/react";

import { Combobox, TextInput, TextInputProps, useCombobox } from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";

import { getHealthGorillaAutocomplete } from "@/health-gorilla/utils/labs";
import { showSystemErrorNotification } from "@/utils/notifications";

export interface LabTestSearchInputProps
  extends Omit<TextInputProps, "onChange" | "defaultValue" | "value"> {
  onChange: (...value: TestCoding[]) => void;
  performingLab: Organization;
  minInputLength?: number;
  defaultValue?: ValueSetExpansionContains;
  value?: ValueSetExpansionContains;
  error?: string;
}

const DROPDOWN_MAX_HEIGHT = 400;

const LabTestSearchInput = ({
  onChange,
  defaultValue,
  minInputLength,
  performingLab,
  ...inputProps
}: LabTestSearchInputProps) => {
  const medplum = useMedplum();
  const [query, setQuery] = useState<string>("");
  const [options, setOptions] = useState<TestCoding[]>([]);
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const debouncedQuery = useDebouncedValue(query, 300)[0];

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const healthGorillaAutocomplete = useMemo(() => getHealthGorillaAutocomplete(medplum), [medplum]);

  useEffect(() => {
    if (debouncedQuery.trim().length === 0) return;

    startLoading();
    (async () => {
      setOptions([]);
      try {
        const response = await healthGorillaAutocomplete<TestSearch>({
          type: "test",
          query: debouncedQuery,
          labId: getIdentifier(performingLab, HEALTH_GORILLA_SYSTEM)!,
        });
        setOptions(response.result);
      } catch (error) {
        showSystemErrorNotification();
        throw error;
      } finally {
        stopLoading();
      }
    })();
  }, [
    medplum,
    healthGorillaAutocomplete,
    debouncedQuery,
    performingLab,
    startLoading,
    stopLoading,
  ]);

  const handleOptionSubmit = useCallback(
    (value: string) => {
      try {
        const coding = options.filter((option) => option.code === value);
        onChange(...coding);
      } finally {
        setQuery("");
        combobox.closeDropdown();
      }
    },
    [onChange, combobox, options],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;

      setQuery(value);
      if (minInputLength && value.length < minInputLength) return;

      combobox.openDropdown();
      combobox.updateSelectedOptionIndex();
    },
    [minInputLength, combobox],
  );

  const { onFocus } = inputProps;
  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      combobox.openDropdown();
      onFocus?.(event);
    },
    [onFocus, combobox],
  );

  const showOptions = !isLoading && options.length > 0;
  const showNoResultsFound = !isLoading && !!debouncedQuery && options.length === 0;
  const hideDropdown = !showOptions && !showNoResultsFound && !isLoading;

  return (
    <Combobox shadow="sm" store={combobox} onOptionSubmit={handleOptionSubmit}>
      <Combobox.Target>
        <TextInput {...inputProps} value={query} onFocus={handleFocus} onChange={handleChange} />
      </Combobox.Target>
      <Combobox.Dropdown
        hidden={hideDropdown}
        mah={DROPDOWN_MAX_HEIGHT}
        style={{ overflowY: "auto" }}
      >
        <Combobox.Options>
          {isLoading && <Combobox.Empty>Searching...</Combobox.Empty>}
          {showOptions &&
            options.map((option, i) => (
              <Combobox.Option value={option.code} key={`${option.code}-${i}`}>
                {option.display}
              </Combobox.Option>
            ))}
          {showNoResultsFound && <Combobox.Empty>No results found</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default LabTestSearchInput;
